import { useTranslation } from "react-i18next";
import { FLOATING_THUMB_CLASS } from "@tellescope/react-hooks";
import { cn } from "../../lib/utils";
import { PaginatedListLoading } from "./PaginatedListLoading";
import { PaginatedListViewport } from "./PaginatedListViewport";
import type { PaginatedListProps } from "./types";
import { usePaginatedList } from "./usePaginatedList";

/**
 * A virtualized scroll container that reports when its start or end is reached, for infinite
 * pagination. Renders only the visible window of `children` via `virtua`.
 *
 * Reaching an edge fires the matching callback once, re-arming only after the list leaves that edge.
 * A list too short to scroll counts as having reached its end, so a first page that underfills the
 * viewport still paginates — gated on `isLoading` so an unsettled list never does.
 *
 * @example
 * ```tsx
 * <PaginatedList isLoading={query.isLoading} onBottomReached={query.fetchNextPage} className="h-96">
 *   {items.map((item) => (
 *     <Item key={item.id} {...item} />
 *   ))}
 * </PaginatedList>
 * ```
 *
 * @example Inside an ancestor that already scrolls, and with a floating scrollbar.
 * ```tsx
 * <PaginatedList scrollOptions={{ element: scrollElement, measureStartMargin: true }}>
 * <PaginatedList scrollOptions={{ type: "floating", fadeWhenClipped: true }}>
 * ```
 */
export const PaginatedList = ({
  children,
  className,
  onBottomReached,
  onTopReached,
  threshold = 50,
  isLoading = false,
  loadingMessage,
  label,
  scrollOptions,
  resetKey,
}: PaginatedListProps) => {
  const { t } = useTranslation();
  const {
    virtualizerRef,
    attachScroller,
    thumbRef,
    handleScroll,
    externalScrollRef,
    startMargin,
    ownsScrolling,
    showFloatingThumb,
    scrollbar,
    isScrolling,
    surfaceStyle,
  } = usePaginatedList({ onBottomReached, onTopReached, threshold, isLoading, scrollOptions });

  if (isLoading) return <PaginatedListLoading className={className} message={loadingMessage} />;

  // The floating thumb overlays the scroller, so it needs a positioned wrapper to sit in. The
  // consumer's `className` moves to that wrapper and the scroller fills it, keeping any height or
  // flex sizing applied to the outermost element either way.
  if (showFloatingThumb) {
    return (
      <div data-slot="paginated-list" className={cn("relative", className)}>
        <PaginatedListViewport
          className="h-full"
          label={label ?? t("List")}
          virtualizerRef={virtualizerRef}
          scrollerRef={attachScroller}
          onScroll={handleScroll}
          externalScrollRef={externalScrollRef}
          startMargin={startMargin}
          ownsScrolling={ownsScrolling}
          scrollbar={scrollbar}
          isScrolling={isScrolling}
          style={surfaceStyle}
          resetKey={resetKey}
        >
          {children}
        </PaginatedListViewport>
        <div ref={thumbRef} className={FLOATING_THUMB_CLASS} />
      </div>
    );
  }

  return (
    <PaginatedListViewport
      className={className}
      label={label ?? t("List")}
      virtualizerRef={virtualizerRef}
      scrollerRef={attachScroller}
      onScroll={handleScroll}
      externalScrollRef={externalScrollRef}
      startMargin={startMargin}
      ownsScrolling={ownsScrolling}
      scrollbar={scrollbar}
      isScrolling={isScrolling}
      style={surfaceStyle}
      resetKey={resetKey}
    >
      {children}
    </PaginatedListViewport>
  );
};
